import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import {
  fetchCashAccounts,
  createCashAccountThunk,
  updateCashAccountThunk,
} from "../../core/redux/slices/cashSlice";
import { getApiErrorMessage } from "../../core/utils/apiError";

const DEFAULT_CASH_NAME = "Cash Counter";
const fmt = (v) => Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 });

// Cash Master is a single settings value exposed through a list-shaped API, not a
// multi-account master (HANDOVER_FRONTEND 4.md §9) — nothing in the system carries a
// cashID, so extra rows can't be tracked separately; the Cash Report just sums every
// Active row's openingBalance into one starting balance. Hence: one editable row, no
// "Add Cash Account" button, and no cash picker on the transaction forms. If the API
// already holds more than one row, they're surfaced read-only below so the summed
// starting balance isn't a surprise, rather than hidden.
const CashSettings = () => {
  const dispatch = useDispatch();
  const { list, loading } = useSelector((s) => s.cash);

  const [openingBalance, setOpeningBalance] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => { dispatch(fetchCashAccounts()); }, [dispatch]);

  const active = list.filter((c) => (c.status || "Active") === "Active");
  const primary = list[0] || null;
  const extras = list.slice(1);
  const seededTotal = active.reduce((sum, c) => sum + Number(c.openingBalance || 0), 0);

  useEffect(() => {
    setOpeningBalance(primary ? String(primary.openingBalance ?? 0) : "");
  }, [primary]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (openingBalance === "" || Number.isNaN(Number(openingBalance))) {
      Swal.fire("Required", "Enter a valid opening balance.", "warning");
      return;
    }
    setSaving(true);
    try {
      const data = { cashName: primary?.cashName || DEFAULT_CASH_NAME, openingBalance: Number(openingBalance) };
      if (primary) {
        await dispatch(updateCashAccountThunk({ id: primary.cashID || primary.id, data })).unwrap();
      } else {
        await dispatch(createCashAccountThunk(data)).unwrap();
      }
      dispatch(fetchCashAccounts());
      Swal.fire({ toast: true, position: "top-end", icon: "success", title: "Cash opening balance saved", showConfirmButton: false, timer: 2000 });
    } catch (err) {
      Swal.fire("Error", getApiErrorMessage(err, "Failed to save the cash opening balance."), "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-wrapper">
      <div className="content">
        <div className="page-header">
          <div className="page-title">
            <h4>Cash</h4>
            <h6>Opening balance for the clinic&apos;s cash counter</h6>
          </div>
          <div className="page-btn">
            <Link to="/reports/cash" className="btn btn-outline-primary">View Cash Report</Link>
          </div>
        </div>

        <div className="row">
          <div className="col-lg-6">
            <div className="card">
              <div className="card-header"><h5 className="mb-0">Cash Opening Balance</h5></div>
              <div className="card-body">
                {loading && !primary ? <p className="text-muted mb-0">Loading...</p> : (
                  <form onSubmit={handleSubmit}>
                    <div className="mb-3">
                      <label className="form-label">Opening Balance (&#8377;) <span className="text-danger">*</span></label>
                      <input type="number" className="form-control" step="0.01"
                        value={openingBalance} onChange={(e) => setOpeningBalance(e.target.value)} placeholder="0.00" />
                      <small className="text-muted">
                        Cash in hand before any transaction was recorded in the system. The Cash Report&apos;s
                        running balance starts from this figure.
                      </small>
                    </div>
                    <button type="submit" className="btn btn-submit" disabled={saving}>
                      {saving ? "Saving..." : "Save"}
                    </button>
                  </form>
                )}
              </div>
            </div>
          </div>

          <div className="col-lg-6">
            <div className="alert alert-info py-2 px-3 small mb-3">
              Every <strong>Cash</strong> payment recorded anywhere in the system &mdash; invoice receipts, income,
              expenses, supplier advances and supplier bill payments &mdash; is picked up by the Cash Report
              automatically. There is nothing to select on those forms, which is why cash has a single opening
              balance here rather than a list of accounts like Bank.
            </div>
            {extras.length > 0 && (
              <div className="alert alert-warning py-2 px-3 small mb-0">
                <strong>{list.length} cash records exist.</strong> Separate cash drawers can&apos;t be tracked
                independently &mdash; the report simply adds every active record&apos;s opening balance together,
                currently seeding it at &#8377;{fmt(seededTotal)}. Only the first record is editable above:
                <ul className="mb-0 mt-1">
                  {extras.map((c) => (
                    <li key={c.cashID || c.id}>{c.cashName} &mdash; &#8377;{fmt(c.openingBalance)} ({c.status || "Active"})</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CashSettings;
